import { useRef, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { CAL_START_HOUR, CAL_TOTAL_HEIGHT, CAL_HEADER_HEIGHT, HOUR_HEIGHT } from '../model/calendarConstants'
import { CalendarHeader } from './CalendarHeader'
import { CalendarTimeAxis } from './CalendarTimeAxis'
import { DayColumn } from './DayColumn'
import { DayStrip } from './DayStrip'
import { UnscheduledTasksBanner } from './UnscheduledTasksBanner'
import { PausePopover, usePauseReschedule } from '../../../features/pause-reschedule'
import { useAuthStore } from '../../../features/auth'
import { useCurrentTimeLine } from '../model/useCurrentTimeLine'
import { useClockMinute } from '../../../shared/hooks/useClockMinute'
import { useBufferedCalendarData } from '../model/useBufferedCalendarData'
import { useCalendarBlockDrag } from '../model/useCalendarBlockDrag'
import { buildDayBlocks, type DayBlocks } from '../model/buildDayBlocks'
import type { WorkerCalendarDays } from '../model/useWorkerCalendarDays'
import { useSchedule, selectTechnicianSchedule, useScheduleFreshness } from '../../../entities/schedule'
import { useTechnicianTasks } from '../../../entities/task'
import { routes } from '../../../shared/config/routes'
import type { ScheduleEntry } from '@myapp/contract'

const TIME_AXIS_WIDTH = 48 // px, w-12

interface Props {
  /**
   * From `useWorkerCalendarDays()`. The page owns the hook — the day window is URL state, a
   * routing concern — and the widget owns everything else: queries, freshness, navigation and
   * the Pause feature. Same split as `DispositionBoardWidget`'s `day`.
   */
  calendar: WorkerCalendarDays
}

export const WorkerCalendarWidget = ({ calendar }: Props) => {
  const { days, dayIsos, from, to, todayIso, view, setView } = calendar

  const navigate = useNavigate()
  const { user } = useAuthStore()
  const currentUserId = Number(user?.id ?? 0)

  const currentTimePx = useCurrentTimeLine()
  // One clock for every column and the drop handler, so a running block has one edge everywhere.
  const nowMs = useClockMinute()
  const didScrollRef = useRef(false)
  const scrollBodyRef = useRef<HTMLDivElement>(null)

  const scheduleQuery = useSchedule(
    { from, to, technicianIds: user?.id ? [Number(user.id)] : undefined },
    { enabled: Boolean(user?.id) },
  )
  const { data: scheduleResult, isFetching, refetch: refetchCalendar } = scheduleQuery

  // One technician's slice of the response, split into the per-kind arrays the widget renders.
  const technicianData = useMemo(
    () => selectTechnicianSchedule(scheduleResult, user?.id),
    [scheduleResult, user?.id],
  )
  const tasksQuery = useTechnicianTasks(user?.id, { from, to })
  const { data: tasksData, isFetching: isTasksFetching, refetch: refetchTasks } = tasksQuery
  const isTransitioning = isFetching || isTasksFetching

  /**
   * One freshness answer for both queries — worst-of, so a current appointment list cannot hide a
   * task list that stopped arriving ten minutes ago. A timestamp rather than an age on purpose:
   * an age would change every tick and re-render three columns of positioned blocks and the drag
   * controller once a minute for nothing. The clock lives in the badge.
   */
  const { lastUpdatedAt, isDisconnected } = useScheduleFreshness([scheduleQuery, tasksQuery])

  /**
   * `refetch()` addresses one observer's current key, so both have to be called. Not
   * `invalidateQueries`, which would also refetch day windows and task lists nobody is watching.
   */
  const handleRefresh = useCallback(() => {
    void Promise.all([refetchCalendar(), refetchTasks()])
  }, [refetchCalendar, refetchTasks])

  const handleToday = useCallback(() => {
    calendar.goToToday()
    void refetchCalendar()
  }, [calendar, refetchCalendar])

  const scheduledTasks = useMemo(() => tasksData?.scheduledTasks ?? [], [tasksData])

  const handleAppointmentClick = useCallback(
    (appointmentId: number) => navigate(routes.worker.appointment(appointmentId)),
    [navigate],
  )

  /** The **absence** id — the owner row, not the schedule entry. See `DayColumn`. */
  const handleAbsenceClick = useCallback(
    (absenceId: number) => navigate(routes.workerZeitmanagement.edit(absenceId)),
    [navigate],
  )

  const handleTaskClick = useCallback(
    (taskId: number) => navigate(routes.worker.task(taskId)),
    [navigate],
  )

  const buffered = useBufferedCalendarData({
    dayIsos,
    technicianData,
    scheduledTasks,
    unscheduledTasks: tasksData?.unscheduledTasks ?? [],
    isTransitioning,
  })

  /**
   * Whether the grid on screen is the one-day layout.
   *
   * Read from the buffered snapshot, not from `view`: after a switch the old columns stay up until
   * the new window's data arrives, and three squeezed columns drawn with the wide block layout —
   * or one wide column holding a three-day snapshot — would be a flash of something that is neither
   * view. The toggle itself reads `view`, so it answers the tap at once.
   */
  const wide = buffered.dayIsos.length === 1

  /**
   * The day a gesture landed on, built exactly as `DayColumn` builds what it draws.
   *
   * Read through a callback rather than memoised per day: a drop can land on any of the three
   * visible columns, and only the handler knows which.
   */
  const dayBlocksFor = useCallback(
    (dayIso: string): DayBlocks =>
      buildDayBlocks({
        technicianData: buffered.technicianData,
        scheduledTasks: buffered.scheduledTasks,
        dayIso,
        nowMs,
      }),
    [buffered.technicianData, buffered.scheduledTasks, nowMs],
  )

  /**
   * Everything a Pause/Abwesenheit move is: drop and tap handling, the conflict gate, the popover
   * state and the mutations. The widget hands it the buffered snapshot and the day builder, and
   * keeps only the pixels — the drag primitive and the pointer-down wiring below.
   */
  const pause = usePauseReschedule({
    technicianData: buffered.technicianData,
    dayBlocksFor,
  })

  // Auto-scroll to current time on mount (once)
  useEffect(() => {
    if (didScrollRef.current) return
    const el = scrollBodyRef.current
    if (!el) return

    const now = new Date()
    const hourDecimal = now.getHours() + now.getMinutes() / 60
    const px = (hourDecimal - CAL_START_HOUR) * HOUR_HEIGHT
    el.scrollTop = Math.max(0, px - 150)
    didScrollRef.current = true
  }, [scrollBodyRef])

  const { drag, startDrag } = useCalendarBlockDrag(pause.handleDrop, pause.handleTap)

  // Prevent pinch-to-zoom while dragging
  useEffect(() => {
    if (!drag) return
    const preventZoom = (e: TouchEvent) => { if (e.touches.length > 1) e.preventDefault() }
    const preventGesture = (e: Event) => e.preventDefault()
    document.addEventListener('touchstart', preventZoom, { passive: false })
    document.addEventListener('touchmove', preventZoom, { passive: false })
    document.addEventListener('gesturestart', preventGesture, { passive: false })
    document.addEventListener('gesturechange', preventGesture, { passive: false })
    return () => {
      document.removeEventListener('touchstart', preventZoom)
      document.removeEventListener('touchmove', preventZoom)
      document.removeEventListener('gesturestart', preventGesture)
      document.removeEventListener('gesturechange', preventGesture)
    }
  }, [drag])

  const onBreakPointerDown = useCallback(
    (dayIso: string, e: React.PointerEvent<HTMLDivElement>, gridEl: HTMLElement, blockTopPx: number, anchorRect: DOMRect) => {
      startDrag('break', dayIso, e, gridEl, blockTopPx, undefined, anchorRect)
    },
    [startDrag],
  )

  const onTMPointerDown = useCallback(
    (entry: ScheduleEntry, e: React.PointerEvent<HTMLDivElement>, gridEl: HTMLElement, blockTopPx: number, anchorRect: DOMRect) => {
      const dayIso = gridEl.dataset.dayIso ?? entry.startsAt.slice(0, 10)
      startDrag('tm', dayIso, e, gridEl, blockTopPx, entry.id ?? undefined, anchorRect)
    },
    [startDrag],
  )

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {/* Day nav — never scrolls */}
      <CalendarHeader
        days={days}
        view={view}
        onViewChange={setView}
        todayIso={todayIso}
        onPrev={calendar.prev}
        onNext={calendar.next}
        onToday={handleToday}
        lastUpdatedAt={lastUpdatedAt}
        isSyncing={isTransitioning}
        isDisconnected={isDisconnected}
        onRefresh={handleRefresh}
      />

      {/*
        Notices, on a budget.

        Each of these three lists renders every item it is given, and on a bad morning a Handwerker
        has six open tasks and three missing Abfahrten. Unbounded, that is nine cards stacked above
        the grid — more than a phone screen, so the calendar this page exists to show was not on it
        at all. The cap is a share of the pane rather than a fixed pixel count so it scales with the
        device, and the overflow scrolls here instead of taking it out of the grid below.

        `overscroll-contain` keeps that scroll from chaining into the grid below once this list
        reaches its end, so a flick that started on a card stays on the cards.
      */}
      <div className="shrink-0 max-h-[38%] overflow-y-auto overscroll-contain">
        <UnscheduledTasksBanner tasks={buffered.unscheduledTasks} onTaskClick={handleTaskClick} />
      </div>

      {/*
        Scrollable body. `min-h-0` because a flex item's default `min-height: auto` lets the 1536px
        grid set the floor, which pushes the bottom of the calendar under the viewport instead of
        scrolling inside it. The bottom padding is the iOS home indicator: `index.html` asks for
        `viewport-fit=cover`, so without it the last hour of the day sits under the bar.
      */}
      <div
        ref={scrollBodyRef}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden touch-pan-y"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* One day: its date, in words, above the grid. Three days: each column carries its own. */}
        {wide && (
          <DayStrip
            day={new Date(`${buffered.dayIsos[0]}T12:00:00`)}
            isToday={buffered.dayIsos[0] === todayIso}
          />
        )}

        <div className="flex" style={{ minHeight: CAL_TOTAL_HEIGHT }}>
          {/* Time axis */}
          <div
            className="shrink-0 border-r border-gray-200 bg-white"
            style={{ width: TIME_AXIS_WIDTH }}
          >
            {!wide && (
              <div className="sticky top-0 z-50 bg-white border-b border-gray-200" style={{ height: CAL_HEADER_HEIGHT }} />
            )}
            <CalendarTimeAxis />
          </div>

          {/* Day columns */}
          <div className="flex flex-1 min-w-0">
            {buffered.dayIsos.map((iso) => (
              <DayColumn
                key={iso}
                dayIso={iso}
                isToday={iso === todayIso}
                wide={wide}
                technicianData={buffered.technicianData}
                currentTimePx={currentTimePx}
                nowMs={nowMs}
                currentUserId={currentUserId}
                onAppointmentClick={handleAppointmentClick}
                onAbsenceClick={handleAbsenceClick}
                scheduledTasks={buffered.scheduledTasks}
                onTaskClick={handleTaskClick}
                drag={drag}
                onBreakPointerDown={onBreakPointerDown}
                onTMPointerDown={onTMPointerDown}
              />
            ))}
          </div>
        </div>
      </div>

      {/*
        Pause popover — portal, rendered once at widget level. The only place this widget reports
        a refusal: there are no confirmation dialogs, because there is nothing to confirm.
      */}
      {pause.editing && (
        <PausePopover
          anchorRect={pause.editing.anchorRect}
          startIso={pause.editing.startIso}
          endIso={pause.editing.endIso}
          advisory={pause.editing}
          rejection={pause.editing.rejection}
          isPending={pause.isPending}
          fixedDurationMin={pause.breakDurationMin}
          onChange={pause.handlePopoverChange}
          onSave={pause.handlePopoverSave}
          onCancel={pause.close}
        />
      )}
    </div>
  )
}
